import React from 'react';
import { LuBookmark, LuX } from 'react-icons/lu';
import { AccentButton } from '../AccentButton';
import { t } from '../../services/HelperService';

const safeString = (v) => (v ?? '').toString();

/** A library key from a file name: lowercase, hyphenated, no extension (the server's SuggestKey settles collisions). */
export const suggestReferenceKey = (fileName) => {
  const stem = safeString(fileName).replace(/\.[^.]+$/, '');
  return stem
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
};

const KEY_OK = /^[a-z0-9][a-z0-9-]*$/;

/**
 * "Keep as reference": the small panel under a chip's bookmark that takes the library
 * key and a one-line description before a session file becomes a design reference.
 * Prefilled from the file (key from the name, description from what the reader saw);
 * Enter keeps, Esc closes, a click outside closes.
 *
 * Props:
 * - attachment: the chip's entry ({ name, fileName, describe, kind })
 * - onKeep({ key, description }): async; the popover shows the error it throws
 * - onClose()
 */
export const KeepAsReferencePopover = ({ attachment, onKeep, onClose }) => {
  const [key, setKey] = React.useState(() => suggestReferenceKey(attachment?.fileName || attachment?.name));
  const [description, setDescription] = React.useState(() => safeString(attachment?.describe).trim().slice(0, 300));
  const [error, setError] = React.useState('');
  const [working, setWorking] = React.useState(false);
  const rootRef = React.useRef(null);
  const keyRef = React.useRef(null);

  React.useEffect(() => {
    keyRef.current?.focus();
    keyRef.current?.select?.();
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) onClose?.();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose?.(); }
    };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);

  const keep = async () => {
    const k = safeString(key).trim().toLowerCase();
    if (!KEY_OK.test(k)) {
      setError(t('A key is lowercase letters, digits and hyphens, starting with a letter or digit.'));
      return;
    }
    try {
      setWorking(true);
      setError('');
      await onKeep?.({ key: k, description: safeString(description).trim() });
    } catch (err) {
      setError(safeString(err?.message) || safeString(err) || t('The reference could not be saved.'));
    } finally {
      setWorking(false);
    }
  };

  const inputStyle = { width: '100%', fontSize: 12, padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 4 };

  return (
    <div
      ref={rootRef}
      data-tagname="keepAsReferencePopover"
      role="dialog"
      aria-label={t('Keep as reference')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey && !working) { e.preventDefault(); keep(); }
      }}
      style={{
        // Above the chip: the strip sits at the foot of the dialog, where below is clipped.
        position: 'absolute', zIndex: 30, bottom: '100%', left: 0, marginBottom: 6, width: 320, padding: 10,
        background: '#fff', border: '1px solid #c7d2fe', borderRadius: 8, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.16)', fontSize: 12, color: '#1e293b',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 6 }}>
        <LuBookmark size={13} style={{ color: '#6f42c1' }} />
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('Keep "{{name}}" as a design reference', { name: safeString(attachment?.name) })}</span>
        <button type="button" title={t('Close')} onClick={onClose} style={{ border: 'none', background: 'none', padding: 2, lineHeight: 0, cursor: 'pointer', color: '#64748b' }}>
          <LuX size={13} />
        </button>
      </div>
      <div style={{ color: '#64748b', marginBottom: 8 }}>
        {t('It goes to the asset library and joins this customisation\'s references, so every later session reads it without it being attached again.')}
      </div>
      <label style={{ display: 'block', marginBottom: 6 }}>
        <span style={{ display: 'block', color: '#64748b', marginBottom: 2 }}>{t('Key')}</span>
        <input ref={keyRef} data-tagname="keepKey" value={key} disabled={working} onChange={(e) => setKey(e.target.value)} style={inputStyle} spellCheck={false} />
      </label>
      <label style={{ display: 'block', marginBottom: 8 }}>
        <span style={{ display: 'block', color: '#64748b', marginBottom: 2 }}>{t('About')}</span>
        <input data-tagname="keepDescription" value={description} disabled={working} onChange={(e) => setDescription(e.target.value)} placeholder={t('One line: what this is')} style={inputStyle} maxLength={300} />
      </label>
      {!!error && <div data-tagname="keepError" style={{ color: '#b71c1c', marginBottom: 6 }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        <AccentButton size="sm" outline tagName="keepCancel" disabled={working} onClick={(e) => { onClose?.(); e?.promise?.(); }}>{t('Cancel')}</AccentButton>
        <AccentButton size="sm" tagName="keepSave" disabled={working} onClick={(e) => { keep().finally(() => e?.promise?.()); }}>{working ? t('Keeping…') : t('Keep')}</AccentButton>
      </div>
    </div>
  );
};
